import { useCallback, useState } from 'react';
import { clsx } from 'clsx';
import * as Y from 'yjs';
import { Button, IconButton, Icon, Tooltip, Popover, icon } from '@nula/core-ui';
import { ColorSwatchPopover } from '../../primitives/formatting/color-swatch-popover';
import { mapToObject, useYjsSnapshot } from '@/lib/sync/use-yjs-snapshot';
import { canvasRoots, type CanvasBinding } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import styles from './node-floating-toolbar.module.css';

type StrokeStyle = 'solid' | 'dashed' | 'dotted';
type StrokeWidth = 1 | 2 | 4;
type Curvature = 'straight' | 'smooth' | 'step';
type Marker = 'none' | 'arrow' | 'dot';
type EndKey = 'markerStart' | 'markerEnd';

const STROKE_STYLES: { value: StrokeStyle; label: string }[] = [
  { value: 'solid',  label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
];

const STROKE_WIDTHS: { value: StrokeWidth; label: string }[] = [
  { value: 1, label: 'Thin' },
  { value: 2, label: 'Medium' },
  { value: 4, label: 'Thick' },
];

const CURVATURES: { value: Curvature; label: string; svg: string }[] = [
  { value: 'straight', label: 'Straight', svg: 'M2 12 L22 12' },
  { value: 'smooth',   label: 'Smooth',   svg: 'M2 16 C8 4, 16 4, 22 16' },
  { value: 'step',     label: 'Step',     svg: 'M2 18 L12 18 L12 6 L22 6' },
];

const MARKERS: { value: Marker; label: string }[] = [
  { value: 'none',  label: 'None' },
  { value: 'arrow', label: 'Arrow' },
  { value: 'dot',   label: 'Dot' },
];

// Type-guards for the literal-union fields so dataMap reads narrow safely
// without `as`. A schema-drifted remote peer can populate these with
// arbitrary strings; falling back to the default is preferable to letting
// the bad value flow into render branches.
const isStrokeStyle = (v: unknown): v is StrokeStyle =>
  v === 'solid' || v === 'dashed' || v === 'dotted';

const isStrokeWidth = (v: unknown): v is StrokeWidth =>
  v === 1 || v === 2 || v === 4;

const isCurvature = (v: unknown): v is Curvature =>
  v === 'straight' || v === 'smooth' || v === 'step';

const isMarker = (v: unknown): v is Marker =>
  v === 'none' || v === 'arrow' || v === 'dot';

function useEdgeData(doc: Y.Doc, edgeId: string, binding: CanvasBinding): Record<string, unknown> {
  return useYjsSnapshot<Record<string, unknown>>(
    () => {
      const data = canvasRoots(doc, binding).edges.get(edgeId)?.get('data');
      return data instanceof Y.Map ? mapToObject(data) : {};
    },
    (refresh) => {
      const edgeMap = canvasRoots(doc, binding).edges.get(edgeId);
      edgeMap?.observeDeep(refresh);
      return () => { edgeMap?.unobserveDeep(refresh); };
    },
    [doc, edgeId],
  );
}

export interface EdgeFormattingToolbarProps {
  doc: Y.Doc;
  clientId: number;
  edgeId: string;
}

/**
 * Embedded formatting controls for a selected edge — parallels
 * NodeFormattingToolbar in shape and chrome. Hosts:
 *   - Color swatch (writes data.stroke)
 *   - Edge style popover (stroke style / width / curvature)
 *   - Endpoint markers popover (Start/End tabs)
 *
 * EdgeFloatingToolbar embeds this the same way NodeFloatingToolbar embeds
 * NodeFormattingToolbar, separated from action buttons (lock/delete) by
 * a `.dividerMajor` from the shared toolbar stylesheet.
 */
export function EdgeFormattingToolbar({ doc, clientId, edgeId }: EdgeFormattingToolbarProps) {
  const binding = useCanvasBinding();
  // Single subscription to the edge — both child popovers receive the
  // resulting dataMap as a prop. Previously each popover called
  // useEdgeData independently, registering 3 redundant observers.
  const dataMap = useEdgeData(doc, edgeId, binding);
  const edge = canvasRoots(doc, binding).edges.get(edgeId);

  const currentStroke = typeof dataMap.stroke === 'string' ? dataMap.stroke : 'var(--outline-variant)';

  const setStroke = useCallback((color: string) => {
    doc.transact(() => {
      const e = canvasRoots(doc, binding).edges.get(edgeId);
      const d = e?.get('data');
      if (!(d instanceof Y.Map)) return;
      d.set('stroke', color);
      e!.set('updatedAt', Date.now());
    }, clientId);
  }, [doc, clientId, edgeId, binding]);

  if (!edge) return null;

  return (
    <>
      <ColorSwatchPopover label="Color" value={currentStroke} onChange={setStroke} />
      <EdgeStylePopover doc={doc} clientId={clientId} edgeId={edgeId} dataMap={dataMap} />
      <EdgeMarkersPopover doc={doc} clientId={clientId} edgeId={edgeId} dataMap={dataMap} />
    </>
  );
}

// ── Edge style popover (stroke style / width / curve) ─────────────────────

interface EdgeStylePopoverProps {
  doc: Y.Doc;
  clientId: number;
  edgeId: string;
  dataMap: Record<string, unknown>;
}

function EdgeStylePopover({ doc, clientId, edgeId, dataMap }: EdgeStylePopoverProps) {
  const binding = useCanvasBinding();
  const [open, setOpen] = useState(false);
  const edge = canvasRoots(doc, binding).edges.get(edgeId);

  const strokeStyle = isStrokeStyle(dataMap.strokeStyle) ? dataMap.strokeStyle : 'solid';
  const strokeWidth = isStrokeWidth(dataMap.strokeWidth) ? dataMap.strokeWidth : 2;
  const curvature   = isCurvature(dataMap.curvature)     ? dataMap.curvature   : 'smooth';

  const writeField = useCallback((key: 'strokeStyle' | 'strokeWidth' | 'curvature', value: unknown) => {
    doc.transact(() => {
      const e = canvasRoots(doc, binding).edges.get(edgeId);
      const d = e?.get('data');
      if (!(d instanceof Y.Map)) return;
      d.set(key, value);
      e!.set('updatedAt', Date.now());
    }, clientId);
  }, [doc, clientId, edgeId, binding]);

  if (!edge) return null;

  const content = (
    <div className={styles.colorPopover}>
      <div className={styles.styleGroup} role="group" aria-label="Stroke style">
        {STROKE_STYLES.map(({ value, label }) => (
          <Tooltip key={value} title={label} placement="top">
            <IconButton
              transparent
              aria-label={label}
              aria-pressed={strokeStyle === value}
              className={clsx(styles.strokeStyleBtn, strokeStyle === value && styles.strokeStyleActive)}
              onClick={() => writeField('strokeStyle', value)}
            >
              <span className={clsx(styles.strokePreview, value !== 'solid' && styles[`stroke_${value}`])} />
            </IconButton>
          </Tooltip>
        ))}
      </div>

      <div className={styles.styleGroup} role="group" aria-label="Stroke width">
        {STROKE_WIDTHS.map(({ value, label }) => (
          <Tooltip key={value} title={label} placement="top">
            <IconButton
              transparent
              aria-label={label}
              aria-pressed={strokeWidth === value}
              className={clsx(styles.strokeStyleBtn, strokeWidth === value && styles.strokeStyleActive)}
              onClick={() => writeField('strokeWidth', value)}
            >
              <span
                className={styles.strokePreview}
                style={{ '--stroke-preview-h': `${value}px` } as React.CSSProperties}
              />
            </IconButton>
          </Tooltip>
        ))}
      </div>

      <div className={styles.styleGroup} role="group" aria-label="Curve">
        {CURVATURES.map(({ value, label, svg }) => (
          <Tooltip key={value} title={label} placement="top">
            <IconButton
              transparent
              aria-label={label}
              aria-pressed={curvature === value}
              className={clsx(styles.strokeStyleBtn, curvature === value && styles.strokeStyleActive)}
              onClick={() => writeField('curvature', value)}
            >
              <svg viewBox="0 0 24 24" width={icon.md} height={icon.md}>
                <path d={svg} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </IconButton>
          </Tooltip>
        ))}
      </div>
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="top" open={open} onOpenChange={setOpen}>
      <Tooltip title="Edge style" placement="top" open={open ? false : undefined}>
        <IconButton transparent aria-label="Edge style" className={styles.btn}>
          <Icon name="conversion_path" size={icon.md} />
        </IconButton>
      </Tooltip>
    </Popover>
  );
}

// ── Endpoint markers popover (Start/End tabs) ─────────────────────────────

interface EdgeMarkersPopoverProps {
  doc: Y.Doc;
  clientId: number;
  edgeId: string;
  dataMap: Record<string, unknown>;
}

function EdgeMarkersPopover({ doc, clientId, edgeId, dataMap }: EdgeMarkersPopoverProps) {
  const binding = useCanvasBinding();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<EndKey>('markerEnd');
  const edge = canvasRoots(doc, binding).edges.get(edgeId);

  const markerStart = isMarker(dataMap.markerStart) ? dataMap.markerStart : 'none';
  const markerEnd   = isMarker(dataMap.markerEnd)   ? dataMap.markerEnd   : 'arrow';
  const activeValue = tab === 'markerStart' ? markerStart : markerEnd;

  const writeMarker = useCallback((key: EndKey, value: Marker) => {
    doc.transact(() => {
      const e = canvasRoots(doc, binding).edges.get(edgeId);
      const d = e?.get('data');
      if (!(d instanceof Y.Map)) return;
      d.set(key, value);
      e!.set('updatedAt', Date.now());
    }, clientId);
  }, [doc, clientId, edgeId, binding]);

  if (!edge) return null;

  const content = (
    <div className={styles.colorPopover}>
      <div className={styles.colorTabs} role="tablist" aria-label="Marker side">
        <Button
          variant="text"
          size="sm"
          role="tab"
          aria-selected={tab === 'markerStart'}
          className={clsx(styles.colorTab, tab === 'markerStart' && styles.colorTabActive)}
          onClick={() => setTab('markerStart')}
        >
          Start
        </Button>
        <Button
          variant="text"
          size="sm"
          role="tab"
          aria-selected={tab === 'markerEnd'}
          className={clsx(styles.colorTab, tab === 'markerEnd' && styles.colorTabActive)}
          onClick={() => setTab('markerEnd')}
        >
          End
        </Button>
      </div>

      <div className={styles.styleGroup} role="group" aria-label={tab === 'markerStart' ? 'Start marker' : 'End marker'}>
        {MARKERS.map(({ value, label }) => (
          <Tooltip key={value} title={label} placement="top">
            <IconButton
              transparent
              aria-label={label}
              aria-pressed={activeValue === value}
              className={clsx(styles.strokeStyleBtn, activeValue === value && styles.strokeStyleActive)}
              onClick={() => writeMarker(tab, value)}
            >
              <MarkerPreview marker={value} side={tab} />
            </IconButton>
          </Tooltip>
        ))}
      </div>
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="top" open={open} onOpenChange={setOpen}>
      <Tooltip title="Endpoint markers" placement="top" open={open ? false : undefined}>
        <IconButton transparent aria-label="Endpoint markers" className={styles.btn}>
          <Icon name="swap_horiz" size={icon.md} />
        </IconButton>
      </Tooltip>
    </Popover>
  );
}

function MarkerPreview({ marker, side }: { marker: Marker; side: EndKey }) {
  const isStart = side === 'markerStart';
  const lineStart = isStart ? 8 : 4;
  const lineEnd   = isStart ? 20 : 16;
  const glyphX    = isStart ? 4 : 20;
  return (
    <svg viewBox="0 0 24 24" width={icon.md} height={icon.md}>
      <line x1={lineStart} y1={12} x2={lineEnd} y2={12} stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
      {marker === 'arrow' && (
        <path
          d={isStart
            ? `M ${glyphX} 12 L ${glyphX + 6} 8 L ${glyphX + 6} 16 Z`
            : `M ${glyphX} 12 L ${glyphX - 6} 8 L ${glyphX - 6} 16 Z`}
          fill="currentColor"
        />
      )}
      {marker === 'dot' && <circle cx={glyphX} cy={12} r={3} fill="currentColor" />}
    </svg>
  );
}
